import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Bell } from "lucide-react";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import { FloatingToast } from "@/components/feedback/FloatingToast";
import { useAlertCounts } from "@/features/alert/hooks";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import NotificationPanel from "./NotificationPanel";
import { getNotificationBellLabel } from "./alertCardRules";
import "./notifications.css";

const PANEL_TRANSITION_MS = 180;
const PANEL_ID = "notification-panel";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [shouldRenderPanel, setShouldRenderPanel] = useState(false);
  const [feedback, setFeedback] = useState<{
    title: string;
    message?: string;
    tone: "error" | "success";
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);
  const timeoutRef = useRef<number | null>(null);
  const countQuery = useAlertCounts();
  const counts = countQuery.data;
  const count = countQuery.error ? undefined : counts?.attention_count;
  const countErrorMessage = countQuery.error
    ? getUserFacingErrorMessage(
        countQuery.error,
        "Não foi possível carregar a contagem de notificações.",
      )
    : null;
  const isDesktop = useMediaQuery("(min-width: 768px)");

  useEffect(() => {
    if (open) {
      setShouldRenderPanel(true);
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    timeoutRef.current = window.setTimeout(() => {
      setShouldRenderPanel(false);
      timeoutRef.current = null;
    }, PANEL_TRANSITION_MS);

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [open]);

  useEffect(() => {
    if (!feedback || feedback.tone === "error") return;
    const timeout = window.setTimeout(() => setFeedback(null), 4200);
    return () => window.clearTimeout(timeout);
  }, [feedback]);

  useEffect(() => {
    if (!open) return;

    function handleClick(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        closePanel();
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      bellRef.current?.focus();
    }

    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  function handleBellClick() {
    setOpen((value) => !value);
  }

  function closePanel() {
    setOpen(false);
    bellRef.current?.focus();
  }

  return (
    <div ref={containerRef} className="notification-bell">
      <button
        ref={bellRef}
        className="notification-bell__trigger"
        data-open={open || undefined}
        onClick={handleBellClick}
        aria-label={getNotificationBellLabel(count)}
        title="Notificações"
        aria-expanded={open}
        aria-controls={PANEL_ID}
      >
        <Bell size={20} strokeWidth={1.5} />
        {count !== undefined && count > 0 ? (
          <span className="notification-bell__badge" aria-hidden="true">
            {count > 99 ? "99+" : count}
          </span>
        ) : null}
      </button>

      {countErrorMessage ? (
        <button
          type="button"
          className="notification-bell__count-retry"
          onClick={() => void countQuery.refetch()}
          disabled={countQuery.isFetching}
          aria-live="polite"
          aria-label={
            countQuery.isFetching
              ? "Tentando atualizar contagem de notificações"
              : `${countErrorMessage} Tentar novamente`
          }
          title={countErrorMessage}
        >
          <AlertTriangle size={12} aria-hidden="true" />
          {countQuery.isFetching ? "Tentando..." : "Tentar"}
        </button>
      ) : null}

      {shouldRenderPanel && (
        <NotificationPanel
          open={open}
          id={PANEL_ID}
          onClose={closePanel}
          placement={isDesktop ? "dropdown" : "viewport"}
          counts={counts}
          onFeedback={setFeedback}
        />
      )}
      {feedback && typeof document !== "undefined"
        ? createPortal(
            <FloatingToast
              title={feedback.title}
              message={feedback.message}
              tone={feedback.tone}
              isDesktop={isDesktop}
              onDismiss={() => setFeedback(null)}
            />,
            document.body,
          )
        : null}
    </div>
  );
}
